import { useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

import { customIcons as icons } from "~/ui";

export interface DropdownOption {
  id: string;
  label: string;
}

interface DropdownInputProps {
  options: DropdownOption[];
  label: (selectedOption: DropdownOption) => React.ReactNode;
  handleOnChange: (option: DropdownOption) => void;
  selected: DropdownOption;
}

export const DropdownInput = ({
  options,
  label,
  selected,
  handleOnChange,
}: DropdownInputProps) => {
  const [optionsListed, setOptionsListed] = useState(false);

  return (
    <Listbox value={selected} onChange={handleOnChange}>
      <div className="relative">
        <div className="outline-hidden inline-flex rounded-md">
          <ListboxButton
            className="data-open:border-b-violet outline-hidden data-open:visible inline-flex items-center border-b border-b-transparent px-3 py-1.5"
            onClick={() => setOptionsListed((prev) => !prev)}
          >
            <div className="text-indigo inline-flex items-center gap-x-1.5 rounded-l-md pr-3">
              <p className="font-sans text-lg">{label(selected)}</p>
            </div>

            {optionsListed ? (
              <ChevronUpIcon
                aria-hidden="true"
                className="text-midnight-gray size-5 forced-colors:text-[Highlight]"
              />
            ) : (
              <ChevronDownIcon
                aria-hidden="true"
                className="text-midnight-gray size-5 forced-colors:text-[Highlight]"
              />
            )}
          </ListboxButton>
        </div>

        <ListboxOptions
          transition
          className="border-light-lavender-gray focus:outline-hidden data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 absolute right-0 top-10 z-10 mt-4 w-52 origin-top-right overflow-hidden rounded-lg border bg-white shadow-sm ring-1 ring-black/5"
        >
          {options.map((option) => (
            <ListboxOption
              key={option.label}
              value={option}
              className="text-indigo group cursor-default select-none p-3 text-[16px]"
            >
              <div className="flex flex-col">
                <div className="flex gap-3">
                  {selected.id === option.id ? (
                    <icons.CheckedCheckbox />
                  ) : (
                    <icons.UncheckedCheckbox />
                  )}

                  <p>{option.label}</p>
                </div>
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
